import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { stockApi, userApi } from '../services/api';
import '../styles/StockDiagnosis.css';

function StockAnalysis({ isLoggedIn, user, stockId = 600519 }) {
  // State management
  const [activeTab, setActiveTab] = useState('technical');
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [peerStocks, setPeerStocks] = useState([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 获取股票诊断数据
    const fetchStockData = async () => {
      setLoading(true);
      try {
        // 使用 stockId 参数获取特定股票数据，与 Java 控制器 /api/stocks/diagnosis/{id} 对应
        const response = await stockApi.getStockDiagnosis(stockId);
        if (response.success) {
          // 处理 API 返回的数据结构
          setStockData(response.data);
          
          // 获取同行业股票数据，与 Java 控制器 /api/stocks/hot 对应
          const hotStocksResponse = await stockApi.getHotStocks();
          if (hotStocksResponse.success) {
            setPeerStocks(hotStocksResponse.data.slice(0, 5));
          }
        } else {
          setError('获取股票数据失败，请稍后重试');
        }
      } catch (error) {
        console.error('获取股票数据失败:', error);
        setError('获取股票数据失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();
    
    // 检查用户订阅状态
    const checkSubscription = async () => {
      if (isLoggedIn) {
        try {
          const response = await userApi.getUserSubscriptions();
          if (response.success) {
            setSubscribed(response.data.diagnosis || false);
          }
        } catch (error) {
          console.error('获取订阅信息失败:', error);
        }
      } else {
        setSubscribed(false);
      }
    };
    
    checkSubscription();
  }, [isLoggedIn, stockId]);

  // 搜索股票
  const handleSearch = async () => {
    if (!searchInput.trim()) return;
    
    setLoading(true);
    try {
      const response = await stockApi.searchStocks(searchInput);
      if (response.success && response.data.length > 0) {
        // 使用搜索到的第一个股票的代码获取详情
        const stockId = response.data[0].id || response.data[0].code;
        const detailResponse = await stockApi.getStockDiagnosis(stockId);
        if (detailResponse.success) {
          setStockData(detailResponse.data);
        } else {
          setError('获取股票详情失败，请稍后重试');
        }
      } else {
        setError('未找到相关股票，请修改搜索条件');
      }
    } catch (error) {
      console.error('搜索股票失败:', error);
      setError('搜索股票失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理登录提示
  const handleLoginRequired = () => {
    setShowLoginPrompt(true);
  };

  // 处理登录
  const handleLogin = () => {
    setShowLoginPrompt(false);
    const loginEvent = new CustomEvent('showLogin');
    window.dispatchEvent(loginEvent);
  };

  // 处理订阅
  const handleSubscribe = () => {
    if (!isLoggedIn) {
      handleLoginRequired();
      return;
    }
    setShowSubscriptionModal(true);
  };

  // 订阅成功
  const handleSubscriptionSuccess = () => {
    setSubscribed(true);
    setShowSubscriptionModal(false);
  };

  // 处理AI诊股
  const handleAIDiagnosis = () => {
    if (!isLoggedIn) {
      handleLoginRequired();
      return;
    }
    
    if (!subscribed) {
      handleSubscribe(); // 如果未订阅，显示订阅弹窗
      return;
    }
    
    // 已登录且已订阅，执行搜索
    handleSearch();
  };

  // 渲染技术指标条
  const renderIndicatorBar = (value, type) => {
    let fillClass = 'medium';
    if (type === 'high') fillClass = 'high';
    if (type === 'low') fillClass = 'low';
    
    return (
      <div className="indicator-bar">
        <div className={`indicator-fill ${fillClass}`} style={{ width: `${value}%` }}></div>
      </div>
    );
  };

  // 加载中状态
  if (loading || !stockData) {
    return (
      <div className="container">
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // 从 API 返回的数据中提取需要的信息，并提供默认值
  const stockName = stockData.stockName || '未知股票';
  const stockCode = stockData.stockCode || '000000';
  const price = stockData.price || 0;
  const priceChange = stockData.priceChange || 0;
  const changePercent = stockData.changePercent || 0;
  const open = stockData.open || 0;
  const prevClose = stockData.prevClose || 0;
  const high = stockData.high || 0;
  const low = stockData.low || 0;
  const volume = stockData.volume || '0';
  const turnover = stockData.turnover || '0';
  const pe = stockData.pe || 0;
  const pb = stockData.pb || 0;
  const industry = stockData.industry || '未知行业';
  const aiAnalysisSummary = stockData.aiAnalysisSummary || '暂无AI分析';
  
  // 解析 JSON 字符串为对象
  let parsedFinancialMetrics = [];
  try {
    if (stockData.financialMetrics) {
      const metricsObj = JSON.parse(stockData.financialMetrics);
      parsedFinancialMetrics = Object.entries(metricsObj).map(([name, data]) => ({
        name,
        value: data.value,
        trend: data.level === 'high' ? 'up' : data.level === 'low' ? 'down' : 'neutral'
      }));
    }
  } catch (e) {
    console.error('解析财务指标失败:', e);
  }
  
  let parsedTechnicalIndicators = [];
  try {
    if (stockData.technicalIndicators) {
      const techObj = JSON.parse(stockData.technicalIndicators);
      if (techObj.指标 && Array.isArray(techObj.指标)) {
        parsedTechnicalIndicators = techObj.指标.map(item => ({
          name: item.name,
          value: item.value,
          signal: item.level === 'high' ? 'buy' : item.level === 'low' ? 'sell' : 'neutral'
        }));
      }
    }
  } catch (e) {
    console.error('解析技术指标失败:', e);
  }
  
  let parsedAiAnalysisPoints = [];
  try {
    if (stockData.aiAnalysisPoints) {
      const points = JSON.parse(stockData.aiAnalysisPoints);
      if (Array.isArray(points)) {
        parsedAiAnalysisPoints = points.map(point => ({
          point: `${point.title}: ${point.content}`,
          sentiment: 'neutral'
        }));
      }
    }
  } catch (e) {
    console.error('解析AI分析要点失败:', e);
  }
  
  // 提取或创建默认的财务指标和技术指标
  const financialIndicators = parsedFinancialMetrics.length > 0 ? parsedFinancialMetrics : [
    { name: '净利润', value: 0, trend: 'neutral' },
    { name: '营收', value: 0, trend: 'neutral' },
    { name: '毛利率', value: 0, trend: 'neutral' }
  ];
  
  const technicalIndicators = parsedTechnicalIndicators.length > 0 ? parsedTechnicalIndicators : [
    { name: 'MACD', value: 0, signal: 'neutral' },
    { name: 'RSI', value: 0, signal: 'neutral' },
    { name: 'KDJ', value: 0, signal: 'neutral' }
  ];
  
  const aiAnalysisPoints = parsedAiAnalysisPoints.length > 0 ? parsedAiAnalysisPoints : [
    { point: '暂无分析要点', sentiment: 'neutral' }
  ];

  return (
    <div className="container">
      {/* 面包屑导航 */}
      <div className="breadcrumbs">
        <Link to="/">首页</Link> <span>&gt;</span> <span>诊股</span>
      </div>

      {/* 股票搜索 */}
      <div className="section-card">
        <div className="section-header">股票搜索</div>
        <div className="section-content">
          <div className="stock-search">
            <input 
              type="text" 
              placeholder="输入股票代码或名称" 
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button onClick={isLoggedIn ? handleSearch : handleAIDiagnosis}>
              {isLoggedIn ? '搜索' : 'AI诊股'}
            </button>
          </div>
        </div>
      </div>

      {/* 股票基本信息 */}
      <div className="section-card">
        <div className="section-header">股票基本信息 - {stockName} ({stockCode})</div>
        <div className="section-content">
          {/* 行情走势图 */}
          <div className="chart-container">
            <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#666'}}>
              {stockName} ({stockCode}) K线图
            </div>
          </div>

          {/* 核心指标 */}
          <div className="stock-info-grid">
            <div className="info-item">
              <div className="info-label">当前价</div>
              <div className={`info-value ${changePercent >= 0 ? 'up' : 'down'}`}>
                {price} <span>{priceChange > 0 ? '+' : ''}{priceChange} ({changePercent > 0 ? '+' : ''}{changePercent}%)</span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">今开</div>
              <div className="info-value">{open}</div>
            </div>
            <div className="info-item">
              <div className="info-label">昨收</div>
              <div className="info-value">{prevClose}</div>
            </div>
            <div className="info-item">
              <div className="info-label">最高</div>
              <div className="info-value up">{high}</div>
            </div>
            <div className="info-item">
              <div className="info-label">最低</div>
              <div className="info-value down">{low}</div>
            </div>
            <div className="info-item">
              <div className="info-label">成交量</div>
              <div className="info-value">{volume}</div>
            </div>
            <div className="info-item">
              <div className="info-label">成交额</div>
              <div className="info-value">{turnover}</div>
            </div>
            <div className="info-item">
              <div className="info-label">市盈率(TTM)</div>
              <div className="info-value">{pe}</div>
            </div>
            <div className="info-item">
              <div className="info-label">市净率</div>
              <div className="info-value">{pb}</div>
            </div>
          </div>
        </div>
      </div>

      {/* AI诊股分析 */}
      <div className="section-card">
        <div className="section-header">AI智能诊股</div>
        <div className="section-content">
          {subscribed || !isLoggedIn ? (
            <>
              {/* AI诊股建议 */}
              <div className="ai-advice">
                <div className="ai-advice-header">
                  <div className="ai-icon"><span>AI</span></div>
                  <div className="ai-title">AI分析建议</div>
                </div>
                <div className="ai-advice-content">
                  {aiAnalysisSummary}
                </div>
              </div>

              {/* 分析标签页 */}
              <div className="analysis-tabs">
                <div 
                  className={`tab ${activeTab === 'technical' ? 'active' : ''}`}
                  onClick={() => setActiveTab('technical')}
                >
                  技术面分析
                </div>
                <div 
                  className={`tab ${activeTab === 'fundamental' ? 'active' : ''}`}
                  onClick={() => setActiveTab('fundamental')}
                >
                  基本面分析
                </div>
                <div 
                  className={`tab ${activeTab === 'sentiment' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sentiment')}
                >
                  情绪分析
                </div>
                <div 
                  className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
                  onClick={() => setActiveTab('ai')}
                >
                  AI诊断要点
                </div>
              </div>

              {/* 标签页内容 */}
              <div className="tab-content">
                {activeTab === 'technical' && (
                  <div className="technical-analysis">
                    <h3>技术指标分析</h3>
                    <div className="indicators-list">
                      {technicalIndicators.map((indicator, index) => (
                        <div key={index} className="indicator-item">
                          <div className="indicator-name">{indicator.name}</div>
                          <div className="indicator-value">{indicator.value}</div>
                          <div className={`indicator-signal ${indicator.signal}`}>
                            {indicator.signal === 'buy' && '买入'}
                            {indicator.signal === 'sell' && '卖出'}
                            {indicator.signal === 'neutral' && '中性'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'fundamental' && (
                  <div className="fundamental-analysis">
                    <h3>财务指标分析</h3>
                    <div className="indicators-list">
                      {financialIndicators.map((indicator, index) => (
                        <div key={index} className="indicator-item">
                          <div className="indicator-name">{indicator.name}</div>
                          <div className="indicator-value">{indicator.value}</div>
                          <div className={`indicator-trend ${indicator.trend}`}>
                            {indicator.trend === 'up' && '↑'}
                            {indicator.trend === 'down' && '↓'}
                            {indicator.trend === 'neutral' && '→'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'sentiment' && (
                  <div className="sentiment-analysis">
                    <h3>市场情绪分析</h3>
                    <div className="sentiment-chart">
                      {/* 情绪分析图表占位 */}
                      <div style={{width:'100%', height:'200px', display:'flex', alignItems:'center', justifyContent:'center', color:'#666'}}>
                        市场情绪分析图表
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'ai' && (
                  <div className="ai-analysis-points">
                    <h3>AI分析要点</h3>
                    <ul className="analysis-points-list">
                      {aiAnalysisPoints.map((point, index) => (
                        <li key={index} className={`analysis-point ${point.sentiment}`}>
                          {point.point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="subscription-prompt">
              <div className="prompt-icon">🔒</div>
              <div className="prompt-title">AI诊股功能需要订阅</div>
              <div className="prompt-description">
                订阅后可获得专业的AI智能诊股分析，助您做出更明智的投资决策。
              </div>
              <button className="subscribe-button" onClick={handleSubscribe}>立即订阅</button>
            </div>
          )}
        </div>
      </div>

      {/* 同行业股票 */}
      <div className="section-card">
        <div className="section-header">同行业股票</div>
        <div className="section-content">
          <div className="peer-stocks">
            <table className="peer-stocks-table">
              <thead>
                <tr>
                  <th>股票代码</th>
                  <th>股票名称</th>
                  <th>最新价</th>
                  <th>涨跌幅</th>
                  <th>成交量</th>
                  <th>市盈率</th>
                </tr>
              </thead>
              <tbody>
                {peerStocks.map((stock, index) => (
                  <tr key={index}>
                    <td>{stock.code || '未知'}</td>
                    <td>{stock.name || '未知'}</td>
                    <td>{stock.price || 0}</td>
                    <td className={stock.changePercent >= 0 ? 'up' : 'down'}>
                      {stock.changePercent > 0 ? '+' : ''}{stock.changePercent || 0}%
                    </td>
                    <td>{stock.volume || '0'}</td>
                    <td>{stock.pe || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 登录提示弹窗 */}
      {showLoginPrompt && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>需要登录</h3>
              <button className="close-button" onClick={() => setShowLoginPrompt(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>请登录后使用AI诊股功能</p>
              <div className="modal-buttons">
                <button className="primary-button" onClick={handleLogin}>立即登录</button>
                <button className="secondary-button" onClick={() => setShowLoginPrompt(false)}>取消</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 订阅弹窗 */}
      {showSubscriptionModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>订阅AI诊股</h3>
              <button className="close-button" onClick={() => setShowSubscriptionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="subscription-plans">
                <div className="plan">
                  <div className="plan-name">月度订阅</div>
                  <div className="plan-price">¥39.9/月</div>
                  <ul className="plan-features">
                    <li>无限AI诊股分析</li>
                    <li>技术面分析</li>
                    <li>基本面分析</li>
                    <li>情绪分析</li>
                  </ul>
                  <button className="subscribe-button" onClick={handleSubscriptionSuccess}>立即订阅</button>
                </div>
                <div className="plan featured">
                  <div className="plan-tag">最受欢迎</div>
                  <div className="plan-name">年度订阅</div>
                  <div className="plan-price">¥399/年</div>
                  <div className="plan-discount">比月付节省20%</div>
                  <ul className="plan-features">
                    <li>所有月度订阅功能</li>
                    <li>优先获取新功能</li>
                    <li>个性化投资建议</li>
                    <li>7x24小时客服支持</li>
                  </ul>
                  <button className="subscribe-button" onClick={handleSubscriptionSuccess}>立即订阅</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockAnalysis;
